import { ChangeEvent } from 'react';

import { Control, Path, PathValue } from 'react-hook-form';
import { z } from 'zod';

import { cn } from '@/lib/utils';

import { Combobox } from '../ui/combobox';
import { FormFieldItem } from '../ui/form';
import { Input } from '../ui/input';

const listFormSchema = z.object({
  name: z.string().min(2, {
    message: 'Required',
  }),
  description: z.string().optional(),
  elements: z.array(z.object({ id: z.string(), value: z.string() })),
});

type ListFormSchemaType = z.infer<typeof listFormSchema>;

function ListSelectField({
  label,
  name,
  control,
  options,
  required,
  renderItem,
  onChange: onChangeOverride,
  labelSize,
  disabled,
}: {
  label: string;
  name: Path<ListFormSchemaType>;
  control: Control<ListFormSchemaType>;
  options: { label: string; value: string }[];
  required?: boolean;
  renderItem?: (option: { value: string; label: string }, isSelected: boolean) => React.ReactNode;
  onChange?: (value: string) => void;
  labelSize?: 'base' | 'small';
  disabled?: boolean;
}) {
  return (
    <FormFieldItem<ListFormSchemaType, Path<ListFormSchemaType>> control={control} name={name} label={label} required={required} labelSize={labelSize}>
      {({ value, onChange, error }) => (
        <Combobox
          aria-label={label}
          value={value as string}
          onChange={(val: string) => {
            if (onChangeOverride) {
              onChangeOverride(val);
            } else {
              onChange(val as PathValue<ListFormSchemaType, Path<ListFormSchemaType>>);
            }
          }}
          options={options}
          className={cn('w-full', error && 'border-default-red')}
          renderItem={renderItem}
          disabled={disabled}
        />
      )}
    </FormFieldItem>
  );
}

function ListInputField({
  label,
  name,
  control,
  placeholder,
  required,
  disabled,
  className,
  labelSize,
}: {
  label: string;
  name: Path<ListFormSchemaType>;
  control: Control<ListFormSchemaType>;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  labelSize?: 'base' | 'small';
}) {
  return (
    <FormFieldItem<ListFormSchemaType, Path<ListFormSchemaType>>
      control={control}
      name={name}
      label={label}
      required={required}
      labelSize={labelSize}
      className={className}>
      {({ value, onChange, error }) => (
        <Input
          placeholder={placeholder ?? `Enter ${label}...`}
          value={value as string}
          onChange={onChange as unknown as (e: ChangeEvent<HTMLInputElement>) => void}
          error={!!error}
          disabled={disabled}
        />
      )}
    </FormFieldItem>
  );
}

export { listFormSchema, ListSelectField, ListInputField };
